import { useState, useEffect, useMemo } from "react";
import { Row, Col, Card, Spinner, ButtonGroup } from "react-bootstrap";
import SntDialog from "@/components/SntDialog/SntDialog";
import SntButton from "@/cleanup/widgets/forms/SntButton";
import { SntSelectWidget } from "@cleanup/widgets/selectors/SntSelect";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";
import wifiCellularMismatchClient, {
  CellTowerAnalysisItem,
  CellTowerAnalysisResponse,
  CellTowerConfidenceLevel,
} from "@/apis/wifiCellularMismatchClient";
import CellTowerAnalysisTable from "./CellTowerAnalysisTable";
import CellTowerAnalysisMap from "./CellTowerAnalysisMap";

interface CellTowerAnalysisDialogProps {
  isShow: boolean;
  onClose: () => void;
}

type ViewMode = "table" | "map";

interface FilterOption {
  value: string;
  label: string;
}

const getDistanceOptions = (): FilterOption[] => [
  { value: "all", label: translate(LanguageKey.all_key) },
  { value: "20000", label: "> 20 km" },
  { value: "50000", label: "> 50 km" },
  { value: "100000", label: "> 100 km" },
];

const getConfidenceOptions = (): FilterOption[] => [
  { value: "all", label: translate(LanguageKey.all_key) },
  { value: "HIGH", label: translate(LanguageKey.confidence_minimum_high_key) },
  { value: "MEDIUM", label: translate(LanguageKey.confidence_minimum_medium_key) },
  { value: "LOW", label: translate(LanguageKey.confidence_minimum_low_key) },
];

const CONFIDENCE_LEVELS: CellTowerConfidenceLevel[] = ["HIGH", "MEDIUM", "LOW"];

const meetsMinimumConfidence = (
  towerConfidence: CellTowerConfidenceLevel | null,
  minimumConfidence: string
): boolean => {
  if (!towerConfidence) return false;
  const towerIndex = CONFIDENCE_LEVELS.indexOf(towerConfidence);
  const minIndex = CONFIDENCE_LEVELS.indexOf(minimumConfidence as CellTowerConfidenceLevel);
  return towerIndex <= minIndex; // Lower index = higher confidence
};

const CellTowerAnalysisDialog = ({
  isShow,
  onClose,
}: CellTowerAnalysisDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CellTowerAnalysisResponse | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("table");

  const distanceOptions = useMemo(() => getDistanceOptions(), []);
  const confidenceOptions = useMemo(() => getConfidenceOptions(), []);

  const [minDistanceFilter, setMinDistanceFilter] = useState<FilterOption | null>(null);
  const [confidenceFilter, setConfidenceFilter] = useState<FilterOption | null>(null);

  // Initialize filters when options are ready
  useEffect(() => {
    if (!minDistanceFilter && distanceOptions.length > 0) {
      setMinDistanceFilter(distanceOptions[0]);
    }
    if (!confidenceFilter && confidenceOptions.length > 0) {
      setConfidenceFilter(confidenceOptions[0]);
    }
  }, [distanceOptions, confidenceOptions, minDistanceFilter, confidenceFilter]);
  const [selectedTower, setSelectedTower] =
    useState<CellTowerAnalysisItem | null>(null);

  useEffect(() => {
    if (isShow && !data) {
      setLoading(true);
      wifiCellularMismatchClient
        .analyzeCellTowers()
        .then(({ data: response }) => {
          setData(response);
        })
        .catch((error) => {
          console.error("Error fetching cell tower analysis:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isShow, data]);

  const filteredTowers = useMemo(() => {
    if (!data?.towers || !minDistanceFilter || !confidenceFilter) return [];

    return data.towers.filter((tower) => {
      // Distance filter
      if (minDistanceFilter.value !== "all") {
        const minDistance = parseInt(minDistanceFilter.value, 10);
        if (tower.distanceMeters === null || tower.distanceMeters < minDistance) {
          return false;
        }
      }

      // Confidence filter (minimum-based: HIGH=only HIGH, MEDIUM=HIGH+MEDIUM, LOW=all)
      if (confidenceFilter.value !== "all") {
        if (!meetsMinimumConfidence(tower.confidence, confidenceFilter.value)) {
          return false;
        }
      }

      return true;
    });
  }, [data?.towers, minDistanceFilter, confidenceFilter]);

  const handleClose = () => {
    onClose();
  };

  return (
    <SntDialog
      isShow={isShow}
      onClose={handleClose}
      title={translate(LanguageKey.cell_tower_analysis_key)}
      isShowSaveButton={false}
      closeTxt={translate(LanguageKey.close_key)}
      minBodyHeight="70vh"
      customModalClassname="cell-tower-analysis-modal"
      bodyClass="d-flex flex-column"
    >
      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : data ? (
        <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
          {/* Summary Stats */}
          <Row className="mb-3">
            <Col>
              <Card className="text-center h-100">
                <Card.Body className="py-2">
                  <Card.Title className="h3 mb-0">{data.totalUniqueTowers.toLocaleString()}</Card.Title>
                  <Card.Text className="text-muted small">{translate(LanguageKey.total_towers_key)}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Card className="text-center h-100">
                <Card.Body className="py-2">
                  <Card.Title className="h3 mb-0">
                    {data.towersWithHarvestedData.toLocaleString()}
                  </Card.Title>
                  <Card.Text className="text-muted small">{translate(LanguageKey.towers_with_harvested_key)}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Card className="text-center h-100">
                <Card.Body className="py-2">
                  <Card.Title className="h3 mb-0 text-success">
                    {data.towersWithGoodAlignment.toLocaleString()}
                  </Card.Title>
                  <Card.Text className="text-muted small">{translate(LanguageKey.towers_good_alignment_key)}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Card className="text-center h-100">
                <Card.Body className="py-2">
                  <Card.Title className="h3 mb-0 text-warning">
                    {data.towersOverThreshold.toLocaleString()}
                  </Card.Title>
                  <Card.Text className="text-muted small">{translate(LanguageKey.towers_over_threshold_key)}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Card className="text-center h-100">
                <Card.Body className="py-2">
                  <Card.Title className="h3 mb-0 text-danger">
                    {data.affectedMismatches.toLocaleString()}
                  </Card.Title>
                  <Card.Text className="text-muted small">{translate(LanguageKey.affected_mismatches_key)}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Filters and View Toggle */}
          <Row className="mb-3 align-items-end">
            {selectedTower && viewMode === "map" ? (
              <>
                <Col className="d-flex align-items-center gap-2">
                  <span>
                    <strong>{translate(LanguageKey.viewing_key)}:</strong>{" "}
                    {selectedTower.distanceMeters !== null
                      ? `${(selectedTower.distanceMeters / 1000).toFixed(1)} km`
                      : "-"}{" "}
                    - mcc: {selectedTower.mcc}, mnc: {selectedTower.mnc}, lac: {selectedTower.area}, cell: {selectedTower.cellId}
                  </span>
                  <SntButton
                    variant="sensolus"
                    onClick={() => setSelectedTower(null)}
                    trackingKey="show_all_lower_key"
                  >
                    {translate(LanguageKey.show_all_lower_key)}
                  </SntButton>
                </Col>
                <Col md="auto">
                  <ButtonGroup>
                    <SntButton
                      className=""
                      onClick={() => setViewMode("table")}
                      trackingKey="table_view_key"
                    >
                      {translate(LanguageKey.table_view_key)}
                    </SntButton>
                    <SntButton
                      className="active"
                      onClick={() => {
                        setSelectedTower(null);
                        setViewMode("map");
                      }}
                      trackingKey="map_key"
                    >
                      {translate(LanguageKey.map_key)}
                    </SntButton>
                  </ButtonGroup>
                </Col>
              </>
            ) : (
              <>
                <Col md={3}>
                  <label className="form-label">{translate(LanguageKey.distance_key)}</label>
                  <SntSelectWidget
                    value={minDistanceFilter}
                    options={distanceOptions}
                    onChange={(option: FilterOption | null) => {
                      if (option) setMinDistanceFilter(option);
                    }}
                  />
                </Col>
                <Col md={3}>
                  <label className="form-label">{translate(LanguageKey.confidence_key)}</label>
                  <SntSelectWidget
                    value={confidenceFilter}
                    options={confidenceOptions}
                    onChange={(option: FilterOption | null) => {
                      if (option) setConfidenceFilter(option);
                    }}
                  />
                </Col>
                <Col md={3}>
                  <span className="text-muted">
                    {translate(LanguageKey.showing_key)}: {filteredTowers.length.toLocaleString()} / {data.towers.length.toLocaleString()}
                  </span>
                </Col>
                <Col md={3} className="text-end">
                  <ButtonGroup>
                    <SntButton
                      className={viewMode === "table" ? "active" : ""}
                      onClick={() => setViewMode("table")}
                    >
                      {translate(LanguageKey.table_view_key)}
                    </SntButton>
                    <SntButton
                      className={viewMode === "map" ? "active" : ""}
                      onClick={() => {
                        setSelectedTower(null);
                        setViewMode("map");
                      }}
                    >
                      {translate(LanguageKey.map_key)}
                    </SntButton>
                  </ButtonGroup>
                </Col>
              </>
            )}
          </Row>

          {/* Content */}
          {viewMode === "table" ? (
            <CellTowerAnalysisTable
              data={filteredTowers}
              onViewTower={(tower) => {
                setSelectedTower(tower);
                setViewMode("map");
              }}
            />
          ) : (
            <CellTowerAnalysisMap
              towers={selectedTower ? [selectedTower] : filteredTowers}
              selectedTower={selectedTower}
              onSelectTower={setSelectedTower}
            />
          )}
        </div>
      ) : (
        <div className="text-center text-muted py-5">
          {translate(LanguageKey.no_data_key)}
        </div>
      )}
    </SntDialog>
  );
};

export default CellTowerAnalysisDialog;
